import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Megaphone, Plus, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { RubyGemMark } from '../../../components/RubyGemMark';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { RubyLoader } from '../../../components/RubyLoader';
import { RubyToken } from '../../../components/RubyToken';
import {
  deleteAiVibe, putAiVibe, rubyAiErrorText, runAiSample,
  type AiActionDto, type AiCatalogResponse, type AiVibeDto, type SampleRunResponse,
} from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import {
  ACTION_GROUP_ORDER, AI_FEATURE_LABELS, DAYS_IN_MONTH, GROUP_LABELS, MONTH_NAMES, WIRED_ACTION_IDS,
  effectiveMultiplier, estimateCharge, findModel, marginFromMultiplier, modelDisplayName, modelRef,
  modelsWithCapability, monthDay, parseModelRef, pct, seasonLabel, tiersUnlocking, typicalRunCost,
  vendorPriceLabel, vibeUnlockedOn, type FamilyTier,
} from './catalogModel';
import {
  buildVibeDraft, buildVibePatch, newVibeDraft, slugifyVibeId, validateVibeDraft, vibeDraftDirty,
  vibeModelCandidates, VIBE_PICTURE_ACTION_IDS, type VibeDraft,
} from './vibeDraft';
import {
  AI_MODEL_USED_FOR, creditsToRubyTokens, formatRubyTokensExact, isVibeInSeason, isVibeNew, tokensLabel, usd,
} from './units';
import { useAiFamily, useRubyAiCatalog, useRubyAiUsage } from './useRubyAi';
import { SafeHtmlPreview } from './safeHtml';
import {
  Badge, Btn, Card, CardHeader, ErrorBanner, FieldLabel, Issues, Modal, Mono, Row, TextArea, TextInput, Toggle,
} from './ui';

/** Badge swatches — brand-safe, no purple / violet / indigo. */
const SWATCHES: Array<{ name: string; hex: string }> = [
  { name: 'Ruby', hex: '#DC143C' }, { name: 'Rose', hex: '#e11d48' }, { name: 'Pine', hex: '#15803d' },
  { name: 'Amber', hex: '#f59e0b' }, { name: 'Orange', hex: '#ea580c' }, { name: 'Teal', hex: '#0d9488' },
  { name: 'Sky', hex: '#0284c7' }, { name: 'Stone', hex: '#a1a1aa' }, { name: 'Black', hex: '#0a0a0a' },
];

const SAMPLES = [
  { name: 'Hand-poured soy candle — Winter Spice', category: 'Home fragrance', brand: 'Lumen & Co', context: '' },
  { name: 'Cold brew coffee concentrate 32oz', category: 'Beverages', brand: 'Northbound', context: '' },
  { name: 'Leather card wallet', category: 'Accessories', brand: '', context: 'Slim, 6 card slots, RFID lining' },
];

/**
 * Careers (text.job_description): the same sample body, read as a job —
 * name = the role, category = the business type, context = the owner's note.
 */
const JOB_SAMPLES = [
  { name: 'Weekend barista', category: 'Coffee shop', brand: 'Foggy Frog Coffee', context: "Part-time, Sat & Sun 7am–3pm, $18/hr plus tips. Latte art a bonus but we'll train." },
  { name: 'Shift lead', category: 'Convenience store', brand: '', context: 'Full-time, both stores, opening and closing, $20/hr' },
  { name: 'Delivery driver', category: 'Pizza restaurant', brand: '', context: 'Own car, evenings, $17/hr plus mileage' },
];

/**
 * Vibes (mockup board 3). A vibe restyles an action's output and carries its
 * own price (D4). The editor saves through the merge-patch endpoint; "Try it"
 * runs the REAL generation for the SAVED vibe, free: it is logged as an admin
 * test run with who ran it, never charged, and counted in vendor spend (D9).
 */
const VibesTab: React.FC<AiTabProps> = ({ onHeaderStats, showToast }) => {
  const [params, setParams] = useSearchParams();
  const { catalog, error, reload } = useRubyAiCatalog();
  const { family } = useAiFamily();
  const { usage } = useRubyAiUsage();

  const vibes = useMemo(
    () => [...(catalog?.vibes ?? [])].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || a.label.localeCompare(b.label)),
    [catalog],
  );
  const param = params.get('vibe');
  const selectedId = param === 'new' ? 'new' : (vibes.some((v) => v.vibeId === param) ? param! : vibes[0]?.vibeId);
  const selected = vibes.find((v) => v.vibeId === selectedId) ?? null;

  const select = (id: string, extra?: Record<string, string>) => {
    const next = new URLSearchParams(params);
    next.set('vibe', id);
    next.delete('action');
    for (const [k, v] of Object.entries(extra ?? {})) next.set(k, v);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!catalog) return;
    onHeaderStats([
      { label: 'Vibes', value: String(catalog.vibes.length), tone: 'white' },
      { label: 'Available today', value: String(catalog.vibes.filter((v) => v.enabled && isVibeInSeason(v.season)).length), tone: 'emerald' },
      { label: 'Marked NEW', value: String(catalog.vibes.filter((v) => isVibeNew(v)).length), tone: 'rose' },
    ]);
  }, [catalog, onHeaderStats]);

  if (error && !catalog) return <ErrorBanner title="Failed to load vibes" detail={error} />;
  if (!catalog) return <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />;

  return (
    <div className="flex flex-col lg:flex-row gap-5">
      <aside className="lg:w-[260px] flex-shrink-0 space-y-2" aria-label="Vibes">
        <Btn kind="soft" className="w-full" onClick={() => select('new')}><Plus size={16} /> New vibe</Btn>
        {vibes.map((v) => {
          const active = v.vibeId === selectedId;
          return (
            <button key={v.vibeId} type="button" onClick={() => select(v.vibeId)} aria-current={active}
              className={`w-full text-left bg-white rounded-xl px-3.5 py-2.5 min-h-[44px] ${active ? 'border-2 border-[#DC143C]' : 'border border-stone-200 hover:border-stone-300'}`}>
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: v.color ?? '#a1a1aa' }} />
                <span className="font-semibold text-sm text-stone-900 truncate flex-1">{v.label}</span>
                {!v.enabled && <Badge tone="stone">OFF</Badge>}
                {isVibeNew(v) && <Badge tone="rubySolid">NEW</Badge>}
              </span>
              <span className="block text-[12px] text-stone-600 mt-0.5 pl-[18px]">
                {typeof v.multiplier === 'number' ? `${v.multiplier} ×` : 'base ×'} · {seasonLabel(v.season)}
                {v.defaultFor.length > 0 && ' · default'}
              </span>
            </button>
          );
        })}
      </aside>
      <div className="flex-1 min-w-0">
        {selectedId === 'new' ? (
          <VibeEditor
            key="new"
            vibe={null}
            initialActionId={params.get('action') ?? undefined}
            catalog={catalog}
            tiers={family?.tiers ?? []}
            usage={usage}
            onSaved={async (id) => { await reload(); select(id); }}
            onDeleted={async () => {}}
            showToast={showToast}
          />
        ) : selected ? (
          <VibeEditor
            key={`${selected.vibeId}-${selected.updatedAt ?? ''}`}
            vibe={selected}
            catalog={catalog}
            tiers={family?.tiers ?? []}
            usage={usage}
            onSaved={async () => { await reload(); }}
            onDeleted={async () => { const n = new URLSearchParams(params); n.delete('vibe'); setParams(n, { replace: true }); await reload(); }}
            showToast={showToast}
          />
        ) : (
          <Card className="p-8 text-center text-sm text-stone-600">No vibes yet. Create the first one.</Card>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const MonthDayPicker: React.FC<{ value: string; onChange: (md: string) => void; label: string; disabled?: boolean }> = ({ value, onChange, label, disabled }) => {
  const [m, d] = value.split('-').map(Number);
  const month = m >= 1 && m <= 12 ? m : 1;
  const day = d >= 1 ? d : 1;
  return (
    <div className="grid grid-cols-[1.3fr_1fr] gap-2">
      <SearchableDropdown
        ariaLabel={`${label} month`} disabled={disabled}
        options={MONTH_NAMES.map((n, i) => ({ value: String(i + 1), label: n }))}
        value={String(month)}
        onChange={(v) => { const nm = Number(v || 1); onChange(monthDay(nm, Math.min(day, DAYS_IN_MONTH[nm - 1]))); }}
        searchPlaceholder="Month…"
      />
      <SearchableDropdown
        ariaLabel={`${label} day`} disabled={disabled}
        options={Array.from({ length: DAYS_IN_MONTH[month - 1] }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
        value={String(Math.min(day, DAYS_IN_MONTH[month - 1]))}
        onChange={(v) => onChange(monthDay(month, Number(v || 1)))}
        searchPlaceholder="Day…"
      />
    </div>
  );
};

const VibeEditor: React.FC<{
  vibe: AiVibeDto | null;
  initialActionId?: string;
  catalog: AiCatalogResponse;
  tiers: FamilyTier[];
  usage: ReturnType<typeof useRubyAiUsage>['usage'];
  onSaved: (vibeId: string) => Promise<void>;
  onDeleted: () => Promise<void>;
  showToast: AiTabProps['showToast'];
}> = ({ vibe, initialActionId, catalog, tiers, usage, onSaved, onDeleted, showToast }) => {
  const original = useMemo(() => (vibe ? buildVibeDraft(vibe) : newVibeDraft(initialActionId)), [vibe, initialActionId]);
  const [draft, setDraft] = useState<VibeDraft>(original);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [idTouched, setIdTouched] = useState(false);
  const set = (p: Partial<VibeDraft>) => setDraft((d) => ({ ...d, ...p }));

  const { actions, models } = catalog;
  const issues = useMemo(
    () => validateVibeDraft(draft, { actions, models, vocabulary: AI_MODEL_USED_FOR, existingIds: catalog.vibes.map((v) => v.vibeId) }),
    [draft, actions, models, catalog.vibes],
  );
  const dirty = vibeDraftDirty(original, draft);

  // Price context: the first assigned action (the live one when there is one).
  const primaryAction: AiActionDto | undefined =
    actions.find((a) => draft.actionIds.includes(a.actionId) && WIRED_ACTION_IDS.has(a.actionId))
    ?? actions.find((a) => draft.actionIds.includes(a.actionId));
  const multNum = draft.multiplier.trim() === '' ? undefined : Number(draft.multiplier);
  const mult = primaryAction ? effectiveMultiplier({ multiplier: multNum }, primaryAction) : multNum;
  const typical = primaryAction ? typicalRunCost(usage, primaryAction.actionId, vibe?.vibeId) : null;
  const est = typical && primaryAction && typeof mult === 'number' && Number.isFinite(mult)
    ? estimateCharge({ vendorCostUsd: typical.costUsd, multiplier: mult, minTokens: primaryAction.minTokens, maxTokens: primaryAction.maxTokens, creditUnitPriceUsd: catalog.config.creditUnitPriceUsd })
    : null;

  const candidates = vibeModelCandidates(models, actions, draft.actionIds, AI_MODEL_USED_FOR);
  const providerLabel = (id: string) => catalog.providers.find((p) => p.providerId === id)?.label ?? id;
  const modelOptions = [
    { value: '', label: 'Action default', subtitle: primaryAction ? modelDisplayName(models, primaryAction.providerId, primaryAction.modelId) : 'The action’s own model' },
    ...candidates.map((m) => ({ value: modelRef(m.providerId, m.modelId), label: m.enabled ? m.label : `${m.label} (disabled)`, subtitle: `${providerLabel(m.providerId)} · ${vendorPriceLabel(m.unitPrices)}` })),
  ];
  if (draft.model && !modelOptions.some((o) => o.value === draft.model)) {
    const p = parseModelRef(draft.model);
    modelOptions.push({ value: draft.model, label: `${modelDisplayName(models, p?.providerId, p?.modelId)} (cannot run every assigned action)`, subtitle: '' });
  }

  // "Model for pictures" (2026-10-04, Ruby Generation Engine) — a SEPARATE
  // override from "Model" above: it writes modelOverrides for BOTH image
  // actions at once (VIBE_PICTURE_ACTION_IDS), independent of which actions
  // this vibe happens to be assigned to, since a vibe may style text and
  // pictures together.
  const pictureCandidates = modelsWithCapability(models, 'draws_images', AI_MODEL_USED_FOR);
  const pictureModelOptions = [
    { value: '', label: 'Action default', subtitle: 'The image action’s own model' },
    ...pictureCandidates.map((m) => ({ value: modelRef(m.providerId, m.modelId), label: m.enabled ? m.label : `${m.label} (Off)`, subtitle: `${providerLabel(m.providerId)} · ${vendorPriceLabel(m.unitPrices)}` })),
  ];
  if (draft.modelForPictures && !pictureModelOptions.some((o) => o.value === draft.modelForPictures)) {
    const p = parseModelRef(draft.modelForPictures);
    pictureModelOptions.push({ value: draft.modelForPictures, label: `${modelDisplayName(models, p?.providerId, p?.modelId)} (cannot draw pictures)`, subtitle: '' });
  }

  const featureIds = Array.from(new Set([
    ...Object.keys(AI_FEATURE_LABELS).filter((f) => f !== 'ai-description'),
    ...tiers.flatMap((t) => t.features ?? []).filter((f) => f.startsWith('ai-vibes')),
    ...(draft.featureId ? [draft.featureId] : []),
  ]));
  const featureOptions = [
    { value: '', label: 'Every tier with the action', subtitle: tiers.length ? tiersUnlocking(tiers, primaryAction?.featureId).join(' · ') : undefined },
    ...featureIds.map((f) => ({
      value: f,
      label: tiers.length ? (tiersUnlocking(tiers, f).join(' · ') || 'No tier yet') : (AI_FEATURE_LABELS[f] ?? f),
      subtitle: `${AI_FEATURE_LABELS[f] ?? f} · ${f}`,
    })),
  ];

  const save = async (extra?: Record<string, unknown>) => {
    setSaving(true);
    setServerError(null);
    const id = draft.isNew ? draft.vibeId : vibe!.vibeId;
    const patch = { ...buildVibePatch(original, draft), ...(extra ?? {}) };
    try {
      const r = await putAiVibe(id, patch);
      const moved = r?.defaultMovedFrom?.length ? ` — default moved from ${r.defaultMovedFrom.join(', ')}` : '';
      showToast(`${draft.label || id} ${r?.created ? 'created' : 'saved'}${moved}`, 'success');
      await onSaved(id);
    } catch (err) {
      setServerError(rubyAiErrorText(err, 'Failed to save the vibe'));
    } finally {
      setSaving(false);
    }
  };

  const announceNow = async () => {
    if (!vibe) return;
    setSaving(true);
    setServerError(null);
    try {
      await putAiVibe(vibe.vibeId, { announceNow: true, ...(draft.announceNew ? { announceNewDays: Number(draft.announceNewDays) } : {}) });
      showToast(`${vibe.label} announced as NEW`, 'success');
      await onSaved(vibe.vibeId);
    } catch (err) {
      setServerError(rubyAiErrorText(err, 'Failed to announce the vibe'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!vibe) return;
    setSaving(true);
    try {
      await deleteAiVibe(vibe.vibeId);
      showToast(`${vibe.label} deleted`, 'success');
      setConfirmDelete(false);
      await onDeleted();
    } catch (err) {
      setServerError(rubyAiErrorText(err, 'Failed to delete the vibe'));
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const toggleAssigned = (actionId: string) => {
    const on = draft.actionIds.includes(actionId);
    set({
      actionIds: on ? draft.actionIds.filter((a) => a !== actionId) : [...draft.actionIds, actionId],
      defaultFor: on ? draft.defaultFor.filter((a) => a !== actionId) : draft.defaultFor,
    });
  };
  const toggleDefault = (actionId: string) => {
    const on = draft.defaultFor.includes(actionId);
    set({ defaultFor: on ? draft.defaultFor.filter((a) => a !== actionId) : [...draft.defaultFor, actionId] });
  };

  const isNew = isVibeNew(vibe ?? {});

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[12px] uppercase tracking-wider text-stone-600">Vibes</p>
          <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2 flex-wrap">
            <span className="w-3 h-3 rounded-full" style={{ background: draft.color }} />
            {draft.label || (draft.isNew ? 'New vibe' : vibe?.vibeId)}
            {isNew && <Badge tone="rubySolid">NEW</Badge>}
            {vibe && isVibeInSeason(vibe.season) && vibe.season && <Badge tone="green">In season</Badge>}
          </h2>
          {vibe && <p className="text-[12px] text-stone-600">vibe id <Mono>{vibe.vibeId}</Mono></p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {vibe && <Btn onClick={() => setConfirmDelete(true)} disabled={saving}><Trash2 size={16} /> Delete</Btn>}
          <Btn onClick={() => setDraft(original)} disabled={saving || !dirty}>Cancel</Btn>
          <Btn kind="primary" onClick={() => void save()} busy={saving} disabled={!dirty || issues.errors.length > 0}>
            <Save size={16} /> {draft.isNew ? 'Create vibe' : 'Save vibe'}
          </Btn>
        </div>
      </div>
      <Issues errors={serverError ? [...issues.errors, serverError] : issues.errors} warnings={dirty ? issues.warnings : []} />

      <div className="grid grid-cols-1 xl:grid-cols-[1.25fr_1fr] gap-4 items-start">
        <div className="space-y-4">
          <Card>
            <CardHeader title="Identity" />
            <div className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel htmlFor="vb-label">Name shown to merchants</FieldLabel>
                <TextInput id="vb-label" value={draft.label} onChange={(e) => {
                  const label = e.target.value;
                  set(draft.isNew && !idTouched ? { label, vibeId: slugifyVibeId(label) } : { label });
                }} placeholder="Black Friday" />
              </div>
              {draft.isNew && (
                <div>
                  <FieldLabel htmlFor="vb-id">Vibe id</FieldLabel>
                  <TextInput id="vb-id" value={draft.vibeId} onChange={(e) => { setIdTouched(true); set({ vibeId: e.target.value.trim() }); }} placeholder="black-friday" />
                </div>
              )}
              <div className="md:col-span-2">
                <FieldLabel>Badge colour</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {SWATCHES.map((s) => (
                    <button key={s.hex} type="button" onClick={() => set({ color: s.hex })} aria-pressed={draft.color.toLowerCase() === s.hex.toLowerCase()} aria-label={s.name} title={s.name}
                      className={`min-h-[44px] min-w-[44px] rounded-lg border inline-flex items-center justify-center ${draft.color.toLowerCase() === s.hex.toLowerCase() ? 'border-[#DC143C] ring-2 ring-rose-200' : 'border-stone-200'}`}>
                      <span className="w-6 h-6 rounded-full" style={{ background: s.hex }} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Toggle on={draft.enabled} onChange={(v) => set({ enabled: v })} label="Vibe on" />
                <span className="text-sm text-stone-700">{draft.enabled ? 'On — merchants can pick it' : 'Off — hidden from merchants'}</span>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="How it writes" />
            <div className="p-4 md:p-5 space-y-4">
              <div>
                <FieldLabel htmlFor="vb-bg">Background — the setting and feeling</FieldLabel>
                <TextArea id="vb-bg" value={draft.background} onChange={(e) => set({ background: e.target.value })} />
              </div>
              <div>
                <FieldLabel htmlFor="vb-ctx">Context — what the reader should take away</FieldLabel>
                <TextArea id="vb-ctx" value={draft.context} onChange={(e) => set({ context: e.target.value })} />
              </div>
              <div>
                <FieldLabel htmlFor="vb-pc">Prompt customisation — added to the action’s base prompt</FieldLabel>
                <TextArea id="vb-pc" className="font-mono text-[12px] min-h-[110px]" value={draft.promptCustomisation} onChange={(e) => set({ promptCustomisation: e.target.value })} />
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap border border-stone-200 rounded-xl px-3 py-2">
                <div className="flex items-center gap-2">
                  <Toggle on={draft.announceNew} onChange={(v) => set({ announceNew: v })} label="Announce as NEW" />
                  <span className="text-sm text-stone-800 font-medium">Announce as NEW to merchants</span>
                </div>
                <div className="flex items-center gap-2">
                  {draft.announceNew && (
                    <div className="w-[130px]">
                      <TextInput aria-label="NEW badge days" inputMode="numeric" value={draft.announceNewDays} suffix="days"
                        onChange={(e) => set({ announceNewDays: e.target.value.replace(/[^\d]/g, '') })} />
                    </div>
                  )}
                  {vibe && (
                    <Btn kind="ghost" onClick={() => void announceNow()} disabled={saving || dirty} title={dirty ? 'Save first' : undefined}>
                      <Megaphone size={16} /> Announce now
                    </Btn>
                  )}
                </div>
              </div>
              {vibe?.announcedAt && <p className="text-[11px] text-stone-600">Last announced {new Date(vibe.announcedAt).toLocaleString()}.</p>}
              <p className="text-[12px] text-stone-600 flex gap-1.5">
                <ShieldCheck size={14} className="flex-shrink-0 mt-0.5 text-emerald-700" />
                Safety rules from the base prompt (no health claims, age notice, no invented specs) always apply and can’t be overridden.
              </p>
            </div>
          </Card>

          <Card>
            <CardHeader title="Where it’s used" hint="One default vibe per action. Making this the default moves the badge off the previous one." />
            <ul className="divide-y divide-stone-100">
              {[...actions].sort((a, b) => ACTION_GROUP_ORDER.indexOf(a.group) - ACTION_GROUP_ORDER.indexOf(b.group)
                || Number(WIRED_ACTION_IDS.has(b.actionId)) - Number(WIRED_ACTION_IDS.has(a.actionId))).map((a) => {
                const assigned = draft.actionIds.includes(a.actionId);
                const isDefault = draft.defaultFor.includes(a.actionId);
                return (
                  <li key={a.actionId} className="px-4 md:px-5 py-1.5 flex items-center justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block text-sm text-stone-800 truncate">
                        <span className="text-stone-500">{GROUP_LABELS[a.group]}</span> · {a.label}
                      </span>
                      <span className={`block text-[12px] ${assigned ? 'text-emerald-700' : 'text-stone-500'}`}>
                        {assigned ? (isDefault ? '✓ assigned · default' : '✓ assigned · not default') : 'not assigned'}
                      </span>
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <Toggle on={assigned} onChange={() => toggleAssigned(a.actionId)} label={`Assign to ${a.label}`} />
                      <Btn kind={isDefault ? 'primary' : 'secondary'} className="!px-3" disabled={!assigned} onClick={() => toggleDefault(a.actionId)}>
                        {isDefault ? 'Default' : 'Make default'}
                      </Btn>
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Price & model" />
            <div className="p-4 md:p-5 space-y-4">
              <div>
                <FieldLabel>Model for text</FieldLabel>
                <SearchableDropdown options={modelOptions} value={draft.model} onChange={(v) => set({ model: v || '' })}
                  placeholder="Action default" searchPlaceholder="Search models…" ariaLabel="Model for text" />
              </div>
              <div>
                <FieldLabel>Model for pictures</FieldLabel>
                <SearchableDropdown options={pictureModelOptions} value={draft.modelForPictures} onChange={(v) => set({ modelForPictures: v || '' })}
                  placeholder="Action default" searchPlaceholder="Search models…" ariaLabel="Model for pictures" />
              </div>
              <div className="flex items-center gap-3">
                <Toggle on={draft.keepScene} onChange={(v) => set({ keepScene: v })} label="Keep the scene" />
                <span className="text-sm text-stone-700">
                  Keep the scene
                  <span className="block text-[12px] text-stone-500">Skips background removal so the vibe's scene stays</span>
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <FieldLabel htmlFor="vb-mult">Multiplier</FieldLabel>
                  <TextInput id="vb-mult" inputMode="decimal" value={draft.multiplier} suffix="×"
                    placeholder={primaryAction ? `${primaryAction.multiplier} (base)` : 'base'}
                    onChange={(e) => set({ multiplier: e.target.value })} />
                </div>
                <div>
                  <FieldLabel>Unlocked on tier</FieldLabel>
                  <SearchableDropdown options={featureOptions} value={draft.featureId} onChange={(v) => set({ featureId: v || '' })}
                    placeholder="Every tier with the action" searchPlaceholder="Search…" ariaLabel="Unlocked on tier" />
                </div>
              </div>
              <div className="border border-stone-200 rounded-xl p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Toggle on={draft.seasonal} onChange={(v) => set({ seasonal: v })} label="Seasonal window" />
                  <span className="text-sm text-stone-800">{draft.seasonal ? 'Seasonal — every year' : 'Always available'}</span>
                </div>
                {draft.seasonal && (
                  <div className="grid grid-cols-1 gap-3">
                    <div><FieldLabel>Available from</FieldLabel><MonthDayPicker label="From" value={draft.fromMonthDay} onChange={(md) => set({ fromMonthDay: md })} /></div>
                    <div><FieldLabel>Until</FieldLabel><MonthDayPicker label="Until" value={draft.toMonthDay} onChange={(md) => set({ toMonthDay: md })} /></div>
                  </div>
                )}
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5 text-[13px] flex items-center justify-between gap-3 flex-wrap">
                <span className="text-stone-700">
                  Our cost ≈ {typical ? usd(typical.costUsd) : '—'} · Tenant pays ≈ {est ? tokensLabel(est.rubyTokens) : '—'}
                  {primaryAction && vibe && tiers.length > 0 && <> · {vibeUnlockedOn(tiers, primaryAction, { featureId: draft.featureId || undefined })}</>}
                </span>
                <span className="font-semibold text-emerald-700">Margin {pct(marginFromMultiplier(mult ?? null))}</span>
              </div>
              {!typical && <p className="text-[11px] text-stone-600">No runs yet to estimate from — run a sample below.</p>}
            </div>
          </Card>

          {vibe ? (
            <SamplePanel vibe={vibe} action={primaryAction} catalog={catalog} dirty={dirty} />
          ) : (
            <Card className="p-4 text-sm text-stone-600">Create the vibe to try it.</Card>
          )}
        </div>
      </div>

      <Modal
        open={confirmDelete} onClose={() => setConfirmDelete(false)} busy={saving}
        title={`Delete ${vibe?.label}?`}
        subtitle="Merchants stop seeing it at once. Spend history keeps its id. Seeded vibes cannot be deleted — switch them off instead."
        footer={<>
          <Btn onClick={() => setConfirmDelete(false)}>Keep it</Btn>
          <Btn kind="primary" onClick={() => void remove()} busy={saving}>Delete vibe</Btn>
        </>}
      >
        <p className="text-sm text-stone-700">This cannot be undone.</p>
      </Modal>
    </div>
  );
};

// ---------------------------------------------------------------------------

const SamplePanel: React.FC<{
  vibe: AiVibeDto; action: AiActionDto | undefined; catalog: AiCatalogResponse; dirty: boolean;
}> = ({ vibe, action, catalog, dirty }) => {
  const isJob = action?.actionId === 'text.job_description';
  const presets = isJob ? JOB_SAMPLES : SAMPLES;
  const [sample, setSample] = useState(presets[0]);
  useEffect(() => { setSample(presets[0]); setResult(null); }, [isJob]); // eslint-disable-line react-hooks/exhaustive-deps
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SampleRunResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<'preview' | 'html'>('preview');

  const wired = !!action && WIRED_ACTION_IDS.has(action.actionId);

  const run = async () => {
    if (!action) return;
    setRunning(true);
    setErr(null);
    setResult(null);
    try {
      setResult(await runAiSample({
        actionId: action.actionId,
        vibeId: vibe.vibeId,
        sample: {
          name: sample.name.trim(), category: sample.category.trim(),
          brand: sample.brand.trim() || null, context: sample.context.trim() || null,
        },
      }));
    } catch (e) {
      setErr(rubyAiErrorText(e, 'The sample run failed'));
    } finally {
      setRunning(false);
    }
  };

  const b = result?.breakdown;
  const stepModel = (pid: string | null, mid: string | null) => (pid && mid ? modelDisplayName(catalog.models, pid, mid) : '—');

  return (
    <Card>
      <CardHeader
        title="Try it"
        hint={action ? <>Runs <strong>{action.label}</strong> with this vibe — the real generation, as a tenant would get it.</> : 'Assign the vibe to an action first.'}
        actions={
          <Btn kind="primary" onClick={() => void run()} busy={running} disabled={!action || !wired || !sample.name.trim() || !sample.category.trim()}>
            {!running && <RubyGemMark size={16} color="currentColor" />} Run sample (free for admins)
          </Btn>
        }
      />
      <div className="p-4 md:p-5 space-y-3">
        {action && !wired && <p className="text-[12px] text-amber-800">“{action.label}” has no generator in code yet, so it cannot be sampled.</p>}
        {dirty && <p className="text-[12px] text-amber-800">You have unsaved edits — the sample runs the SAVED vibe.</p>}
        <div className="flex flex-wrap gap-2">
          {presets.map((s) => (
            <button key={s.name} type="button" onClick={() => setSample(s)} aria-pressed={sample.name === s.name}
              className={`min-h-[44px] px-3 rounded-lg border text-[12px] text-left ${sample.name === s.name ? 'border-[#DC143C] bg-[#FFF1F3] text-[#9F1239]' : 'border-stone-300 text-stone-700 bg-white'}`}>
              {s.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><FieldLabel htmlFor="sm-name">{isJob ? 'Role' : 'Sample product'}</FieldLabel><TextInput id="sm-name" value={sample.name} onChange={(e) => setSample({ ...sample, name: e.target.value })} /></div>
          <div><FieldLabel htmlFor="sm-cat">{isJob ? 'Business type' : 'Category'}</FieldLabel><TextInput id="sm-cat" value={sample.category} onChange={(e) => setSample({ ...sample, category: e.target.value })} /></div>
          <div><FieldLabel htmlFor="sm-brand">{isJob ? 'Business name (optional)' : 'Brand (optional)'}</FieldLabel><TextInput id="sm-brand" value={sample.brand} onChange={(e) => setSample({ ...sample, brand: e.target.value })} /></div>
          <div><FieldLabel htmlFor="sm-ctx">{isJob ? "Owner's note (optional)" : 'Notes (optional)'}</FieldLabel><TextInput id="sm-ctx" value={sample.context} onChange={(e) => setSample({ ...sample, context: e.target.value })} /></div>
        </div>

        {result && !running && (
          <div className="flex gap-2">
            {(['preview', 'html'] as const).map((m) => (
              <button key={m} type="button" onClick={() => setView(m)} aria-pressed={view === m}
                className={`min-h-[44px] px-3 rounded-lg text-[12px] font-semibold border ${view === m ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-stone-300 text-stone-700'}`}>
                {m === 'preview' ? 'Preview' : 'HTML'}
              </button>
            ))}
          </div>
        )}
        <div className={`relative border border-stone-200 rounded-xl bg-stone-50 min-h-[120px] p-3 text-sm text-stone-800 ${view === 'html' ? 'whitespace-pre-wrap font-mono text-[12px]' : ''}`}>
          {running ? (
            <span className="flex items-center gap-2 text-stone-600"><RubyToken size={22} drawing /> Writing…</span>
          ) : result ? (view === 'preview' ? <SafeHtmlPreview html={result.output} /> : result.output)
            : <span className="text-stone-500">{isJob ? 'The generated job post appears here.' : 'The generated description appears here.'}</span>}
        </div>
        {err && <Issues errors={[err]} />}

        {b && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-stone-900">What this run cost</p>
            <div className="border border-stone-200 rounded-xl px-3 py-1">
              <Row label="Model used" value={stepModel(b.providerId, b.modelId)} />
              <Row label="Input / output tokens" value={`${formatRubyTokensExact(b.inputTokens)} / ${formatRubyTokensExact(b.outputTokens)}`} />
              <Row label="Web search calls" value={b.searchCalls} />
              <Row label="Our vendor cost" value={usd(b.vendorCostUsd, 4)} />
              <Row label="× Multiplier" value={`${b.multiplier} ×`} />
              <Row strong label="A tenant would pay" value={
                <span className="inline-flex items-center gap-1.5">
                  <RubyToken size={16} /> {formatRubyTokensExact(b.rubyTokens)} Ruby Tokens
                </span>
              } />
            </div>
            {b.unclampedCredits !== b.credits && (
              <p className="text-[12px] text-stone-600">
                Before the charge limits: {formatRubyTokensExact(creditsToRubyTokens(b.unclampedCredits))} Ruby Tokens — the action’s
                {b.unclampedCredits < b.credits ? ` minimum (${formatRubyTokensExact(b.minRubyTokens)})` : ` maximum (${formatRubyTokensExact(b.maxRubyTokens)})`} decided the price.
              </p>
            )}
            {b.steps.length > 1 && (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead><tr className="text-stone-600 text-left"><th className="py-1 pr-3 font-semibold">Step</th><th className="py-1 pr-3 font-semibold">Model</th><th className="py-1 pr-3 font-semibold">Tokens in / out</th><th className="py-1 pr-3 font-semibold">Searches</th><th className="py-1 font-semibold text-right">Cost</th></tr></thead>
                  <tbody>
                    {b.steps.map((s, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        <td className="py-1 pr-3">{s.type ?? `step ${i + 1}`}</td>
                        <td className="py-1 pr-3">{stepModel(s.providerId, s.modelId)}</td>
                        <td className="py-1 pr-3">{s.inputTokens} / {s.outputTokens}</td>
                        <td className="py-1 pr-3">{s.searchCalls}</td>
                        <td className="py-1 text-right">{usd(s.vendorCostUsd, 4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[12px] text-stone-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Logged as an admin test run — <strong>{result!.actorId}</strong>, not charged to any tenant, counted in vendor spend.
            </p>
            {findModel(catalog.models, b.providerId, b.modelId) == null && (
              <p className="text-[11px] text-stone-500">Model {b.providerId}/{b.modelId} is not in the catalogue.</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default VibesTab;
